import { useCallback, useEffect, useRef } from "react";
import { Location, Space } from "../data";
import { useAnalytics } from "../lib/analytics/analytics";
import { Order, Quote, BookingType, SpaceOffering, OrderableType } from "./api";
import { MembershipDetail, PartnerOffering } from "../data/partner_offering";
import { usePrevious } from "../hooks/use_previous";
import { getFieldFromObject } from "../offering_v2/utils/get_field_from_object";

export function useTrackProductViewedEcommerceEvent() {
  const analytics = useAnalytics();

  return useCallback(
    (
      name: string | undefined,
      space?: Space,
      location?: Location,
      quote?: Quote,
      spaceOffering?: SpaceOffering,
      partnerOffering?: PartnerOffering
    ) => {
      const products = getOnDemandOrderProducts(
        name,
        quote,
        space,
        location,
        spaceOffering,
        partnerOffering
      );
      const revenue = quote?.totalPrice;
      const currency = space?.currencyCode || location?.currencyCode || "";

      if (products && products.length > 0) {
        analytics.ecommerceEvent({
          event: "Product Viewed",
          revenue: revenue,
          currency: currency,
          products: products,
          city: location?.city,
          country: location?.country,
        });
      }
    },
    [analytics]
  );
}

export function useTrackProductViewedEcommerceEventOnMount(
  name: string | undefined,
  space?: Space,
  location?: Location,
  quote?: Quote | null,
  spaceOffering?: SpaceOffering,
  partnerOffering?: PartnerOffering
) {
  const trackedRef = useRef(false);
  const prevSpace = usePrevious(space);
  const prevSpaceOffering = usePrevious(spaceOffering);
  const trackProductViewedEcommerceEvent =
    useTrackProductViewedEcommerceEvent();
  useEffect(() => {
    if (
      !!(
        name &&
        space &&
        location &&
        quote &&
        (trackedRef.current === false ||
          prevSpace?.id !== space.id ||
          prevSpaceOffering?.id !== spaceOffering?.id)
      )
    ) {
      trackProductViewedEcommerceEvent(
        name,
        space,
        location,
        quote,
        spaceOffering,
        partnerOffering
      );
      trackedRef.current = true;
    }
  }, [
    trackProductViewedEcommerceEvent,
    name,
    space,
    location,
    quote,
    prevSpace,
    spaceOffering,
    prevSpaceOffering,
    partnerOffering,
  ]);
}

export function useTrackCheckoutStartedEcommerceEvent() {
  const analytics = useAnalytics();

  return useCallback(
    (
      name: string,
      quote?: Quote,
      space?: Space,
      location?: Location,
      spaceOffering?: SpaceOffering,
      partnerOffering?: PartnerOffering
    ) => {
      const id = space?.id;
      const currency = space?.currencyCode || location?.currencyCode || "";
      const revenue = quote?.totalPrice;
      const tax = quote?.taxLine?.amount;
      const coupon = quote?.promotionLine?.code;
      const products = getOnDemandOrderProducts(
        name,
        quote,
        space,
        location,
        spaceOffering,
        partnerOffering
      );

      if (products && products.length > 0) {
        analytics.ecommerceEvent({
          event: "Checkout Started",
          id,
          currency,
          revenue,
          tax,
          coupon,
          partnerOfferingSlug: partnerOffering?.slug,
          city: location?.city,
          country: location?.country,
          products: products,
        });
      }
    },
    [analytics]
  );
}

export function useTrackCheckoutStartedEcommerceEventOnMount(
  name: string | undefined,
  quote?: Quote,
  space?: Space,
  location?: Location,
  spaceOffering?: SpaceOffering,
  partnerOffering?: PartnerOffering
) {
  const trackedRef = useRef(false);
  const trackCheckoutStartedEcommerceEvent =
    useTrackCheckoutStartedEcommerceEvent();
  const prevSpace = usePrevious(space);
  const prevSpaceOffering = usePrevious(spaceOffering);

  useEffect(() => {
    if (
      name &&
      space &&
      location &&
      quote &&
      (trackedRef.current === false ||
        prevSpace?.id !== space.id ||
        prevSpaceOffering?.id !== spaceOffering?.id)
    ) {
      trackCheckoutStartedEcommerceEvent(
        name,
        quote,
        space,
        location,
        spaceOffering,
        partnerOffering
      );
      trackedRef.current = true;
    }
  }, [
    trackCheckoutStartedEcommerceEvent,
    name,
    quote,
    space,
    location,
    prevSpace,
    spaceOffering,
    prevSpaceOffering,
    partnerOffering,
  ]);
}

export function useTrackOrderCompletedEcommerceEvent(
  partnerOffering: PartnerOffering
) {
  const analytics = useAnalytics();

  return useCallback(
    (
      name: string | undefined,
      order: Order,
      space?: Space,
      location?: Location,
      spaceOffering?: SpaceOffering
    ) => {
      const orderItem = order?.orderItems[0];
      const isBookingType = orderItem.type === OrderableType.Booking;
      let startDate: string | undefined;
      let endDate: string | undefined;
      let startTime: string | undefined;
      let endTime: string | undefined;
      let duration: number | undefined;
      if (isBookingType && orderItem.details.type === BookingType.Hourly) {
        startDate = orderItem.details.date;
        endDate = orderItem.details.date;
        startTime = orderItem.details.startTime;
        endTime = orderItem.details.endTime;
        duration = orderItem.details.hours;
      } else if (
        isBookingType &&
        orderItem.details.type === BookingType.Daily
      ) {
        startDate = orderItem.details.startDate;
        endDate = orderItem.details.endDate;
        duration = orderItem.details.days;
      }

      analytics.ecommerceEvent({
        event: "Order Completed",
        id: order.id,
        currency: order.currencyCode || location?.currencyCode || "",
        revenue: order?.totalPrice,
        revenueInUsd: order?.totalPriceInUsd,
        tax: order?.taxLine?.amount,
        coupon: order.promotionLine?.code,
        city: location?.city,
        country: location?.country,
        partnerOfferingSlug: partnerOffering.slug,
        products: getOnDemandOrderProducts(
          name,
          order,
          space,
          location,
          spaceOffering,
          partnerOffering
        ),
        merchantId:
          location?.googleAction?.active && location?.googleAction?.entityId,
        email: order.email,
        name: order.fullName,
        company: { name: order.companyName },
        createdAt: new Date(),
        phone: order.phoneNumber,
        pageURL: window.location.href,
        template: partnerOffering.template,
        date: getIsoDate(startDate), // for use in analytics
        startDate,
        endDate,
        startTime,
        endTime,
        duration,
      });
    },
    [analytics, partnerOffering]
  );
}

function getOnDemandOrderProducts(
  name: string | undefined,
  orderOrQuote: Order | Quote | undefined,
  space?: Space,
  location?: Location,
  spaceOffering?: SpaceOffering,
  partnerOffering?: PartnerOffering
) {
  if (!orderOrQuote || !name) return [];

  return orderOrQuote.orderItems.map((oi) => {
    let quantity = oi.quantity;
    const details = getFieldFromObject("details", oi);
    const type = getFieldFromObject("type", details);
    const days = getFieldFromObject("days", details);

    // We want to record the date of the booking so that we can choose to send
    // marketing emails based on the booking date instead of created date
    const date = getIsoDate(details.date || details.startDate);

    if (type === BookingType.Daily) {
      quantity = days * oi.quantity;
    }

    const category = spaceOffering?.productTypeName || space?.productType?.name;
    const category2 =
      spaceOffering?.productCategory ||
      (oi.type === "AddOn" ? "access_add_on" : space?.productType?.type);

    return {
      id: space?.id,
      sku: space?.id,
      name: name,
      category: category,
      category2: category2,
      category3: name + ", " + location?.name,
      category4: spaceOffering ? name + ", " + spaceOffering?.name : undefined,
      slug: partnerOffering?.slug,
      brand: location?.name,
      price: oi.unitPrice,
      quantity: quantity,
      date: date,
      currency: oi.orderCurrency,
    };
  });
}

export function useTrackMembershipViewed() {
  const analytics = useAnalytics();

  return useCallback(
    (
      partnerOffering: PartnerOffering,
      membership: MembershipDetail | undefined,
      quote?: Quote | null
    ) => {
      if (!quote) return;

      const quoteItem = quote?.quoteItems?.[0];

      analytics.ecommerceEvent({
        event: "Product Viewed",
        currency: quote.currency,
        revenue: quote.totalPrice,
        coupon: quote?.promotionLine?.code,
        products: [
          {
            id: membership?.id,
            sku: membership?.id,
            name: partnerOffering.offeringTitle,
            category: membership?.productTypeName,
            category2: membership?.productCategory,
            category3:
              partnerOffering.offeringTitle +
              ", " +
              membership?.locatable?.name,
            category4: undefined,
            slug: partnerOffering.slug,
            brand: membership?.locatable?.name,
            price: quoteItem?.subTotal,
            date: new Date().toISOString(),
            quantity: quoteItem?.quantity,
            coupon: quoteItem?.promotion?.code,
            discount: quoteItem?.promotionAmount,
            currency: quote.currency,
          },
        ],
      });
    },
    [analytics]
  );
}

export function useTrackMembershipViewedEcommerceEventOnMount(
  partnerOffering: PartnerOffering,
  membership: MembershipDetail | undefined,
  quote?: Quote | null
) {
  const analytics = useAnalytics();
  const prevMembership = usePrevious(membership);
  const trackedRef = useRef(false);
  const trackMembershipViewed = useTrackMembershipViewed();

  useEffect(() => {
    if (
      quote &&
      membership &&
      (trackedRef.current === false || prevMembership?.id !== membership.id)
    ) {
      trackMembershipViewed(partnerOffering, membership, quote);

      trackedRef.current = true;
    }
  }, [
    analytics,
    partnerOffering,
    membership,
    quote,
    prevMembership,
    trackMembershipViewed,
  ]);
}

export function useTrackMembershiCheckoutStartedEcommerceEventOnMount(
  partnerOffering: PartnerOffering,
  membership: MembershipDetail | undefined,
  quote?: Quote | null
) {
  const analytics = useAnalytics();
  const prevMembership = usePrevious(membership);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (
      quote &&
      membership &&
      (trackedRef.current === false || prevMembership?.id !== membership.id)
    ) {
      const quoteItem = quote?.quoteItems?.[0];

      analytics.ecommerceEvent({
        event: "Checkout Started",
        currency: quote.currency,
        revenue: quote.totalPrice,
        coupon: quote?.promotionLine?.code,
        products: [
          {
            id: membership?.id,
            sku: membership?.id,
            name: partnerOffering.offeringTitle,
            category: membership?.productTypeName,
            category2: membership?.productCategory,
            category3:
              partnerOffering.offeringTitle +
              ", " +
              membership?.locatable?.name,
            category4: undefined,
            brand: membership?.locatable?.name,
            price: quoteItem?.subTotal,
            date: new Date().toISOString(),
            slug: partnerOffering.slug,
            quantity: quoteItem?.quantity,
            coupon: quoteItem?.promotion?.code,
            discount: quoteItem?.promotionAmount,
            currency: quote.currency,
          },
        ],
      });

      trackedRef.current = true;
    }
  }, [analytics, partnerOffering, membership, quote, prevMembership]);
}

export function useTrackMembershipPurchase() {
  const analytics = useAnalytics();

  return useCallback(
    (
      order: Order,
      membership: MembershipDetail | undefined,
      location: Location | undefined,
      partnerOffering: PartnerOffering
    ) => {
      const orderItem = order?.orderItems[0];

      analytics.ecommerceEvent({
        event: "Order Completed",
        id: order.id,
        currency: order.currencyCode,
        revenue: order.totalPrice,
        revenueInUsd: order?.totalPriceInUsd,
        tax: order.taxLine?.amount,
        coupon: order.promotionLine?.code,
        email: order.email,
        name: order.fullName,
        company: {
          name: order.companyName,
        },
        createdAt: new Date(),
        phone: order.phoneNumber,
        pageURL: window.location.href,
        template: partnerOffering.template,
        city: location?.city,
        country: location?.country,
        merchantId:
          location?.googleAction?.active && location?.googleAction?.entityId,
        products: [
          {
            id: membership?.id,
            sku: membership?.id,
            name: partnerOffering.offeringTitle,
            category: membership?.productTypeName,
            category2: membership?.productCategory,
            category3:
              partnerOffering.offeringTitle +
              ", " +
              membership?.locatable?.name,
            category4: undefined,
            slug: partnerOffering.slug,
            brand: membership?.locatable?.name,
            price: orderItem?.subTotal,
            date: new Date().toISOString(),
            quantity: orderItem?.quantity,
            coupon: order.promotionLine?.code,
            discount: order.promotionLine?.discount,
            currency: order.currencyCode,
          },
        ],
        bookingType:
          orderItem.type === OrderableType.Booking
            ? orderItem.details.type
            : undefined,
      });
    },
    [analytics]
  );
}

function getIsoDate(date: string | undefined) {
  if (!date) return undefined;

  const bookingDate = new Date(date);
  const now = new Date();
  bookingDate.setHours(now.getHours());
  bookingDate.setMinutes(now.getMinutes());

  return bookingDate.toISOString();
}
